import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { Know, KnowFormat } from 'interfaces/Know'
import Link from 'next/link'
import styled from 'styled-components'
import KnowService from 'services/KnowService'
import { ApiError } from 'services/ApiService'
import { trackPromise } from 'react-promise-tracker'
import LoadingArea from 'components/elements/LoadingArea'
import theme from 'stories/utils/theme'
import { ButtonText } from 'stories/elements/Typography/Text'
import KnowLikes from 'assets/icons/heart-stroke.svg'
import KnowViews from 'assets/icons/views-stroke.svg'
import PDFIcon from 'assets/icons/pdf.svg'
import FlashcardIcon from 'assets/icons/flashcard_big.svg'
import { knowRoute } from 'lib/constants/routes'
import { useRouter } from 'next/router'
import { isKnowNotIndexable } from 'lib/features/Know/knowSSR'
import KnowImpressionTracking from 'lib/tracking/KnowImpressionTracking'
import NextImage from 'components/elements/NextImage'

const Container = styled.div<{ hideShadow?: boolean; isLarge?: boolean }>`
    width: ${({ isLarge }) => (isLarge ? '334px' : '250px')};
    height: auto;
    min-height: 250px;

    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    background-color: ${theme.colors.backgroundTransparent};
    border-radius: ${theme.borderRadius.webappCard};
    color: ${theme.colors.grayScaleSecondary};

    cursor: pointer;

    .top {
        padding: 15px;
        display: grid;
        grid-template-columns: 1fr auto;
    }

    .title {
        display: grid;
        grid-gap: 5px;
    }
    .bottom {
        padding: 5px 15px 10px 15px;
        display: grid;
        grid-gap: 5px;
        .stats {
            display: flex;
            gap: 15px;
            padding-bottom: 2.5px;
            p {
                border: 1px solid transparent;
                font-family: var(--font-inter) !important;
                font-variation-settings: 'opsz' 32 !important;
                font-weight: 400;
                font-size: 11px;
                line-height: 16px;
                letter-spacing: 0.5px;
                color: ${theme.colors.grayScaleSecondary};
            }
            .icon {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 5px;
            }
        }
    }
    a {
        text-decoration: none !important;
    }
`

const KnowImage = styled(NextImage)`
    object-fit: cover;
    object-position: top;
    border-radius: ${theme.borderRadius.webappCard};
`

const EmptyPreview = styled.div<{ isLarge?: boolean }>`
    height: ${({ isLarge }) => (isLarge ? '334px' : '250px')};
    width: ${({ isLarge }) => (isLarge ? '334px' : '250px')};
    background: ${theme.colors.backgroundTransparent};
    border-radius: ${theme.borderRadius.webappCard};

    display: grid;
    place-content: center;
    margin-bottom: 5px;

    svg {
        fill: white;
        width: 50px;
        height: 50px;
    }
`

const Headline = styled.h3`
    font-feature-settings: 'calt', 'cv01', 'cv02', 'cv03', 'cv04', 'cv05', 'cv08', 'cv09';
    font-variation-settings: 'opsz' 32 !important;
    font-family: var(--font-inter);
    margin: 0;
    padding: 0;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    padding: 0;
    color: ${theme.colors.white};
    font-size: 14px;
    font-weight: 500 !important;
    height: fit-content;
    line-height: 18px;
    letter-spacing: 0.3px;
    height: 40px;
`

const loadingAreas = {
    know: 'knowContent',
}

interface Props {
    knowUuid: string
    startSnapScroll?: boolean
    know?: Know
    referrerScreen?: string
    disabledLinking?: boolean
    showTitleAsSubHeadline?: boolean
    path?: string
    properties?: {
        disableBoxShadow?: boolean
        isLarge?: boolean
    }
}

const HomepageKnowCard = (props: Props) => {
    const [know, setKnow] = useState<Know | null>(props.know ?? null)
    const [imageError, setImageError] = useState<boolean>(true)
    const router = useRouter()
    const referrerScreen = props.referrerScreen
    const { disabledLinking } = props

    const fetchData = useCallback(() => {
        if (props.know) return

        trackPromise(
            KnowService.get(props.knowUuid)
                .then((know) => {
                    setKnow(know)
                })
                .catch((error) => {
                    if (error instanceof ApiError) {
                        console.error(error)
                    } else {
                        throw error
                    }
                }),
            loadingAreas.know
        )
    }, [props.know, props.knowUuid])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    useEffect(() => {
        setImageError(false)
    }, [know])

    if (!know) return null

    const knowPath = props.path ?? knowRoute(know.slug, { referrerScreen })

    const redirectToKnow = () => !disabledLinking && router.push(knowPath)

    const KnowWrapper = (props: { children: ReactNode }) =>
        isKnowNotIndexable(know) || disabledLinking ? (
            <div onClick={redirectToKnow}>{props.children}</div>
        ) : (
            <Link href={knowRoute(know.slug, { referrerScreen })} passHref prefetch={false} legacyBehavior>
                <a>{props.children}</a>
            </Link>
        )

    const showFlashcardsIcon = know.knowFormat === KnowFormat.Flashcards

    const handleImageError = () => {
        setImageError(true)
    }

    return (
        <div>
            {know ? <KnowImpressionTracking know={know} referrerScreen={referrerScreen} /> : null}

            <KnowWrapper>
                <Container hideShadow={props.properties?.disableBoxShadow} isLarge={props.properties?.isLarge}>
                    <LoadingArea area={loadingAreas.know}>
                        <div className="image">
                            {know.thumbnailLargeUrl && (!imageError || know.seoTitle?.startsWith('Line')) ? (
                                <KnowImage
                                    src={know.thumbnailLargeUrl}
                                    alt={`Know ${know.seoTitle ?? know.title} thumbnail`}
                                    width={props.properties?.isLarge ? 334 : 250}
                                    height={props.properties?.isLarge ? 334 : 250}
                                    loading="lazy"
                                    onError={handleImageError}
                                />
                            ) : (
                                <EmptyPreview isLarge={props.properties?.isLarge}>
                                    {showFlashcardsIcon ? <FlashcardIcon /> : <PDFIcon />}
                                </EmptyPreview>
                            )}
                        </div>
                        <div className="bottom">
                            <Headline>{know.seoTitle ?? know.title}</Headline>

                            <div className="stats">
                                <div className="icon">
                                    <KnowViews />
                                    <ButtonText color={theme.colors.grayScaleSecondary}>{know.views}</ButtonText>
                                </div>
                                <div className="icon">
                                    <KnowLikes />
                                    <ButtonText color={theme.colors.grayScaleSecondary}>{know.likes}</ButtonText>
                                </div>
                            </div>
                        </div>
                    </LoadingArea>
                </Container>
            </KnowWrapper>
        </div>
    )
}

export default HomepageKnowCard
